import cloneDeep from 'lodash.clonedeep';
import { call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import uuid from 'uuid';

import {
  DELETE_AUDIO_FROM_PROJECT,
  UPDATE_AUDIO_CONFIG,
  SPLIT_AUDIO_TRACK,
  getUserAudioSuccess,
  getUserAudioFailure,
  GET_USER_AUDIO_REQUESTED,
  DELETE_AUDIO_ASSET,
  closeDeleteAudioAssetModal,
  SET_AUDIO_CLIP_TITLE
} from 'js/actionCreators/audioActions';
import { setSelectedAudioClip, updateScribe, LOAD_SCRIBE_BY_ID_SUCCESS } from 'js/actionCreators/scribeActions';
import { showError } from 'js/actionCreators/uiActions';
import { appServices } from 'js/shared/helpers/app-services/AppServices';
import { editorSetEditPanelMode } from 'js/actionCreators/editorActions';
import { sendErrorToSentry } from 'js/logging';

import { getScribeById } from './selectors';
import audioLibraryUploadSagas from './audioLibraryUploadSagas';
import audioLibrarySagas from './audioLibrarySagas';
import { updateAudioLayers } from './sagaHelpers/updateAudioLayers';
import { cleanupUnusedAudioLayersAndIds } from './sagaHelpers/cleanupUnusedAudioLayersAndIds';
import { updateAudioClipTitleSaga } from './audioSagas/updateAudioClipTitle';

export function* splitAudioTrack({ scribeId, id, splitTime }) {
  // Split the audio clip at the given time relative to the start of the original audio clip
  const minimumDuration = 0.1;
  const activeScribe = yield select(getScribeById, scribeId);
  const newActiveScribe = cloneDeep(activeScribe);
  const audioIndexToSplit = newActiveScribe.audioClips.findIndex(clip => clip.id === id);
  const audioClip = newActiveScribe.audioClips[audioIndexToSplit];
  const layer = newActiveScribe.audioLayers.find(layer => layer.audioClipIds.includes(id));
  const newAudioClip = {
    ...audioClip,
    instanceDuration: splitTime - audioClip.startOffset
  };

  let originalName = audioClip.filename;
  const splitName = audioClip.filename.split('_');

  if (!isNaN(parseInt(splitName[0]))) {
    originalName = originalName.substring(originalName.indexOf('_') + 1);
  }
  const instanceCount = activeScribe.audioClips.filter(clip => clip.filename.includes(originalName)).length;
  let newName = instanceCount + '_' + originalName;
  const newAudioClip2 = {
    ...audioClip,
    id: uuid(),
    filename: newName,
    startTime: newAudioClip.startTime + newAudioClip.instanceDuration,
    startOffset: newAudioClip.instanceDuration + audioClip.startOffset,
    instanceDuration: audioClip.instanceDuration - newAudioClip.instanceDuration
  };
  // Don't split if the new audio clips are too short
  if (newAudioClip2.instanceDuration < minimumDuration || newAudioClip.instanceDuration < minimumDuration) {
    return;
  }

  layer.audioClipIds.push(newAudioClip2.id);
  newActiveScribe.audioClips.splice(audioIndexToSplit, 1, newAudioClip, newAudioClip2);

  yield put(updateScribe(newActiveScribe));
  yield put(setSelectedAudioClip(newAudioClip2));
}

function* updateAudioConfig({ scribeId, id, config, layerId }) {
  const activeScribe = yield select(getScribeById, scribeId);
  const newActiveScribe = cloneDeep(activeScribe);
  const audioIndexToReplace = newActiveScribe.audioClips.findIndex(clip => {
    return clip.id === id;
  });

  const audioClip = {
    ...newActiveScribe.audioClips[audioIndexToReplace],
    ...config
  };
  newActiveScribe.audioClips.splice(audioIndexToReplace, 1, audioClip);

  if (layerId) {
    updateAudioLayers(newActiveScribe, audioClip, layerId);
  }

  yield put(updateScribe(newActiveScribe));
  yield put(setSelectedAudioClip(audioClip));
}

export function* deleteAudioFromProject({ scribeId, id: audioClipId }) {
  const activeScribe = yield select(getScribeById, scribeId);

  let newActiveScribe = cloneDeep(activeScribe);

  const audioIndexToRemove = newActiveScribe.audioClips.findIndex(clip => clip.id === audioClipId);
  newActiveScribe.audioClips.splice(audioIndexToRemove, 1);

  let audioLayerIndex = -1;
  const audioLayer = newActiveScribe.audioLayers.find((layer, index) => {
    const isMatchingLayer = layer.audioClipIds.includes(audioClipId);
    if (isMatchingLayer) {
      audioLayerIndex = index;
    }
    return isMatchingLayer;
  });

  if (audioLayer && audioLayerIndex !== -1) {
    const shouldDeleteLayer = audioLayer.audioClipIds.length === 1;
    if (shouldDeleteLayer) {
      // Remove the entire layer
      newActiveScribe.audioLayers.splice(audioLayerIndex, 1);

      // Remove the layer ID from projectAudioLayerIds and scene audioLayerIds
      newActiveScribe.projectAudioLayerIds = (newActiveScribe.projectAudioLayerIds || []).filter(
        layerId => layerId !== audioLayer.id
      );
      newActiveScribe.scenes.forEach(scene => {
        scene.audioLayerIds = (scene.audioLayerIds || []).filter(layerId => layerId !== audioLayer.id);
      });
    } else {
      // Remove the clip ID from the layer
      audioLayer.audioClipIds = audioLayer.audioClipIds.filter(clipId => clipId !== audioClipId);
    }
  }

  newActiveScribe = cleanupUnusedAudioLayersAndIds(newActiveScribe);

  yield put(setSelectedAudioClip(null));
  yield put(editorSetEditPanelMode(null));
  yield put(updateScribe(newActiveScribe));
}

function* deleteAudioAsset() {
  const assetId = yield select(state => state.audio.deleteAudioAssetId);
  yield call(appServices.deleteAsset, assetId);
  yield put(closeDeleteAudioAssetModal());
  yield call(getUserAudio);
}

function* getUserAudio() {
  try {
    const assets = yield call({ fn: appServices.getAssetsList, context: appServices }, 'audio');
    const audio = assets.map(audio => {
      return {
        ...audio
      };
    });
    yield put(getUserAudioSuccess(audio));
  } catch (error) {
    sendErrorToSentry(error);
    yield put(getUserAudioFailure(error));
    yield put(showError(error));
  }
}

function* audioSagas() {
  yield fork(audioLibraryUploadSagas);
  yield fork(audioLibrarySagas);
  yield takeLatest(DELETE_AUDIO_FROM_PROJECT, deleteAudioFromProject);
  yield takeLatest(DELETE_AUDIO_ASSET, deleteAudioAsset);
  yield takeLatest(UPDATE_AUDIO_CONFIG, updateAudioConfig);
  yield takeLatest(SPLIT_AUDIO_TRACK, splitAudioTrack);
  yield takeEvery(GET_USER_AUDIO_REQUESTED, getUserAudio);
  yield takeLatest(LOAD_SCRIBE_BY_ID_SUCCESS, getUserAudio);
  yield takeLatest(SET_AUDIO_CLIP_TITLE, updateAudioClipTitleSaga);
}

export default audioSagas;
